import { Box } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SysColumn from "app/class/sys-column";
import { FilterBuilder } from "modules/record/record-producer/components/record-producer-fields/condition-field/FilterBuilder";
import { LogicalNode } from "modules/record/record-producer/types/query.types";
import { ChoiceOption } from "modules/record/shared/hooks/useChoiceOptions";
import DataPicker from "./DataPicker";
import { DataPickerMeta, ExtraGroup } from "./data-picker/dataPicker";
import { ScopeOption } from "./fluss-builder-types";

export interface FlussConditionBuilderProps {
    value: LogicalNode | string | null | undefined;
    onChange: (value: LogicalNode | string) => void;
    // `record.` of this condition when it is not the process anchor table (work record).
    recordTable?: string;
    recordSourceLabel?: string;
    // Observed-table conditions see only the record's columns.
    recordOnly?: boolean;
    // Extra `payload.<key>` fields (message start): not part of the catalog.
    payloadFields?: ScopeOption[];
    readOnly?: boolean;
}

// FilterBuilder needs a column (type, options, reference table) to pick the operator list and the
// value editor. The DataPicker reports it for the references in use (type, referenced table,
// CHOICE options and the real sys_columns.id so lookups keep working).
export const columnForMeta = (meta: DataPickerMeta, order: number, options?: ChoiceOption[]) => new SysColumn({
    id: meta.columnId || `fluss-data-${meta.path}`,
    sys_table_id: "__fluss_condition_context",
    source_sys_table_id: "__fluss_condition_context",
    active: true,
    label: meta.label,
    name: meta.path,
    column_type: meta.type || "TEXT",
    column_subtype: "",
    referenced_table_name: meta.referencedTable || "",
    order,
    is_display: false,
    default_value: "",
    is_hidden: false,
    is_mandatory: false,
    reference_filter: "",
    depends_on_column_id: "",
    currency_code: "",
    currency_decimal_places: null,
    created_at: new Date(0),
    updated_at: new Date(0),
    options,
});

export const FlussConditionBuilder = ({
    value,
    onChange,
    recordTable,
    recordSourceLabel,
    recordOnly = false,
    payloadFields = [],
    readOnly = false,
}: FlussConditionBuilderProps) => {
    const { t } = useTranslation();
    const [resolved, setResolved] = useState<Record<string, DataPickerMeta>>({});
    const register = useCallback((meta: DataPickerMeta) => {
        setResolved((previous) => {
            const current = previous[meta.path];
            return current && current.type === meta.type && current.referencedTable === meta.referencedTable
                && current.columnId === meta.columnId && current.label === meta.label
                && JSON.stringify(current.options) === JSON.stringify(meta.options)
                ? previous : { ...previous, [meta.path]: meta };
        });
    }, []);

    // Legacy BPMN routes store a single condition. Adapt it for FilterBuilder only while
    // reading; the caller's XML remains untouched until the user actually edits the route.
    const builderValue = useMemo(() => {
        let parsed: any = value;
        if (typeof value === "string") {
            try { parsed = value ? JSON.parse(value) : null; } catch (_error) { parsed = null; }
        }
        return parsed?.type === "condition"
            ? { id: "fluss-legacy-root", type: "logical" as const, operator: "and" as const,
                conditions: [{ ...parsed, id: parsed.id || "fluss-legacy-condition" }] }
            : value;
    }, [value]);

    const completeLabel = String(t("fluss.builder.condition.value.complete", { defaultValue: "Concluída" }));
    const displayOptions = useCallback((options?: ChoiceOption[] | null) => options?.map((option) =>
        option.value.toLowerCase() === "complete" ? { ...option, label: completeLabel } : option
    ), [completeLabel]);

    const columns = useMemo(
        () => Object.values(resolved).map((meta, index) => columnForMeta(meta, index, displayOptions(meta.options))),
        [resolved, displayOptions]
    );
    const extraGroups = useMemo<ExtraGroup[] | undefined>(() => payloadFields.length === 0 ? undefined : [{
        key: "payload",
        label: String(t("fluss.builder.scope.payload", { defaultValue: "Dados recebidos" })),
        outputs: payloadFields.map((field) => ({
            path: `payload.${field.name}`, label: field.label, type: field.type, referencedTable: field.referencedTableName,
        })),
    }], [payloadFields, t]);
    // The condition builder tolerates a stale `resolved` map: a field that is not resolved yet is
    // rendered as TEXT until the picker reports its real type.
    const fieldLabel = String(t("fluss.builder.condition.field", { defaultValue: "Campo da condição" }));

    return (
        <Box component="fieldset" className="fluss-condition-builder" disabled={readOnly} sx={{
            border: 0, p: 0, m: 0, minInlineSize: 0, width: "100%",
            "& .fluss-condition-row .MuiFormControl-root": { minWidth: 0, width: "100%" },
            "& .fluss-condition-row .MuiInputBase-root": { minWidth: 0 },
        }}>
            <FilterBuilder
                value={builderValue}
                columns={columns}
                allColumns={columns}
                tables={[]}
                maxReferenceDepth={1}
                flatFields
                allowedOperators={["eq", "neq", "in", "not_in", "empty", "not_empty", "contains", "not_contains", "stw", "edw", "gt", "gte", "lt", "lte", "on", "not_on", "between", "relative_last", "relative_next"]}
                compact
                addConditionLabel={String(t("fluss.builder.condition.add", { defaultValue: "Condição" }))}
                andLabel={String(t("fluss.builder.condition.and", { defaultValue: "AND" }))}
                orLabel={String(t("fluss.builder.condition.or", { defaultValue: "OR" }))}
                fieldAriaLabel={fieldLabel}
                presentation={{
                    fieldLabel: String(t("fluss.builder.condition.field_label", { defaultValue: "Campo" })),
                    operatorLabel: String(t("fluss.builder.condition.operator_label", { defaultValue: "Operador" })),
                    operatorLabelFor: (label) => label === "next"
                        ? String(t("fluss.node_panel.service.param.filter.comparison_operator.relative_next", { defaultValue: "nos próximos" }))
                        : label === "last"
                        ? String(t("fluss.node_panel.service.param.filter.comparison_operator.relative_last", { defaultValue: "nos últimos" }))
                        : undefined,
                    valueLabel: String(t("fluss.builder.condition.value_label", { defaultValue: "Valor" })),
                    dynamicValueColumns: columns,
                    fixedValueLabel: String(t("fluss.builder.value.fixed", { defaultValue: "Valor fixo" })),
                    dataValueLabel: String(t("fluss.builder.value.data", { defaultValue: "Dado" })),
                    resolveColumn: (field) => columns.find((column) => column.name === field),
                    renderFieldPicker: ({ value: field, onChange: pick, ariaLabel }) => (
                        <DataPicker
                            value={field || undefined}
                            allowFixed={false}
                            recordTable={recordTable}
                            recordSourceLabel={recordSourceLabel}
                            recordOnly={recordOnly}
                            extraGroups={extraGroups}
                            ariaLabel={ariaLabel || fieldLabel}
                            onResolved={register}
                            onChange={(next, meta) => {
                                if (meta) register(meta);
                                pick(typeof next === "string" ? next : "");
                            }}
                        />
                    ),
                    renderDataValuePicker: ({ value: reference, onChange: pick, ariaLabel, column }) => (
                        <DataPicker
                            value={reference || undefined}
                            allowFixed={false}
                            recordTable={recordTable}
                            recordSourceLabel={recordSourceLabel}
                            recordOnly={recordOnly}
                            extraGroups={extraGroups}
                            expectedType={column?.column_type}
                            ariaLabel={ariaLabel}
                            onChange={(next, meta) => {
                                if (meta) register(meta);
                                pick(typeof next === "string" ? next : "");
                            }}
                        />
                    ),
                }}
                onChange={onChange}
            />
        </Box>
    );
};

export default FlussConditionBuilder;
